import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Video, Save, Loader2, Play, Plus, Trash2, ChevronUp, ChevronDown,
  CheckCircle2, AlertCircle, Clock, GripVertical, MousePointerClick,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

// Metadatos de las acciones disponibles (deben coincidir con engine.run_action del backend)
const OPS = {
  goto: { label: "Ir a página", fields: [{ k: "path", label: "Ruta", ph: "/app/dashboard" }] },
  click: { label: "Hacer clic", fields: [{ k: "selector", label: "Selector", ph: '[data-testid="..."]' }] },
  click_first: { label: "Clic en el 1.º (prefijo)", fields: [{ k: "prefix", label: "Prefijo data-testid", ph: "trip-row-" }] },
  hover: { label: "Señalar (sin clic)", fields: [{ k: "selector", label: "Selector", ph: '[data-testid="..."]' }] },
  type_in: { label: "Escribir texto", fields: [{ k: "selector", label: "Selector", ph: '[data-testid="..."]' }, { k: "text", label: "Texto", ph: "Texto a escribir" }] },
  scroll: { label: "Desplazar", fields: [{ k: "dy", label: "Píxeles (dy)", type: "number", ph: "300" }] },
  esc: { label: "Pulsar Escape", fields: [] },
};
const OP_KEYS = Object.keys(OPS);
const clone = (x) => JSON.parse(JSON.stringify(x || []));

function reorder(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const c = [...arr];
  [c[i], c[j]] = [c[j], c[i]];
  return c;
}

const STATUS_UI = {
  running: { icon: Loader2, cls: "text-amber-600", label: "Generando…", spin: true },
  done: { icon: CheckCircle2, cls: "text-emerald-600", label: "Generado" },
  error: { icon: AlertCircle, cls: "text-destructive", label: "Error" },
  idle: { icon: Clock, cls: "text-muted-foreground", label: "Sin generar" },
};

function StatusBadge({ status }) {
  const s = STATUS_UI[status] || STATUS_UI.idle;
  const I = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.cls}`} data-testid="vs-status-badge">
      <I className={`h-3.5 w-3.5 ${s.spin ? "animate-spin" : ""}`} />{s.label}
    </span>
  );
}

export default function VideoScriptsManager() {
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState(null);
  const [steps, setSteps] = useState([]);
  const [dirty, setDirty] = useState(false);
  const prevStatus = useRef(null);

  const list = useQuery({ queryKey: ["video-scripts"], queryFn: async () => (await api.get("/ayuda/video-scripts")).data });
  const videos = list.data?.videos || [];
  const selected = videos.find((v) => v.key === selectedKey) || null;

  useEffect(() => {
    if (!selectedKey && videos.length) setSelectedKey(videos[0].key);
  }, [videos, selectedKey]);

  // Cargar pasos al cambiar de vídeo (no pisa ediciones en curso del mismo vídeo)
  useEffect(() => {
    if (selected) { setSteps(clone(selected.steps)); setDirty(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const status = useQuery({
    queryKey: ["video-status", selectedKey],
    queryFn: async () => (await api.get(`/ayuda/video-scripts/${selectedKey}/status`)).data,
    enabled: !!selectedKey,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 3000 : false),
  });
  const isRunning = status.data?.status === "running";
  const jobStatus = status.data?.status;
  const effStatus = isRunning
    ? "running"
    : (jobStatus && jobStatus !== "idle" ? jobStatus : (selected?.video_exists ? "done" : "idle"));

  useEffect(() => {
    const s = status.data?.status;
    if (prevStatus.current === "running" && s !== "running") {
      qc.invalidateQueries({ queryKey: ["video-scripts"] });
      qc.invalidateQueries({ queryKey: ["ayuda-overview"] });
      qc.invalidateQueries({ queryKey: ["ayuda-article"] });
      if (s === "done") toast.success("Vídeo generado y adjuntado al manual");
      else if (s === "error") toast.error("Error al generar el vídeo. Revisa el registro.");
    }
    prevStatus.current = s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.status]);

  const save = useMutation({
    mutationFn: async () => (await api.put(`/ayuda/video-scripts/${selectedKey}`, { steps })).data,
    onSuccess: () => { toast.success("Guion guardado"); setDirty(false); qc.invalidateQueries({ queryKey: ["video-scripts"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const gen = useMutation({
    mutationFn: async () => (await api.post(`/ayuda/video-scripts/${selectedKey}/generate`)).data,
    onSuccess: () => { toast.info("Generación iniciada. Tardará unos minutos…"); status.refetch(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  // ---- edición de pasos / acciones ----
  const upd = (next) => { setSteps(next); setDirty(true); };
  const setStep = (si, patch) => upd(steps.map((s, i) => (i === si ? { ...s, ...patch } : s)));
  const addStep = () => upd([...steps, { narration: "", actions: [{ op: "goto", path: "/app/dashboard", wait: 1500 }] }]);
  const delStep = (si) => upd(steps.filter((_, i) => i !== si));
  const moveStep = (si, dir) => upd(reorder(steps, si, dir));

  const setActions = (si, actions) => setStep(si, { actions });
  const addAction = (si) => setActions(si, [...(steps[si].actions || []), { op: "click", selector: "", wait: 900 }]);
  const delAction = (si, ai) => setActions(si, steps[si].actions.filter((_, i) => i !== ai));
  const moveAction = (si, ai, dir) => setActions(si, reorder(steps[si].actions, ai, dir));
  const setActionField = (si, ai, k, v) =>
    setActions(si, steps[si].actions.map((a, i) => (i === ai ? { ...a, [k]: v } : a)));
  const setActionOp = (si, ai, op) =>
    setActions(si, steps[si].actions.map((a, i) => (i === ai ? { op, wait: a.wait } : a)));

  if (list.isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]" data-testid="video-scripts-manager">
      {/* Lista de vídeos */}
      <Card className="h-fit lg:sticky lg:top-4">
        <CardContent className="space-y-1 p-3">
          <p className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vídeos</p>
          {videos.map((v) => {
            const active = v.key === selectedKey;
            return (
              <button key={v.key} onClick={() => setSelectedKey(v.key)} data-testid={`vs-video-${v.key}`}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${active ? "bg-accent text-accent-foreground" : "hover:bg-muted"}`}>
                <Video className="h-4 w-4 shrink-0 text-primary" />
                <span className="flex-1 truncate">{v.title}</span>
                {v.job?.status === "running" && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-amber-600" />}
                {v.video_exists && v.job?.status !== "running" && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {/* Editor del vídeo seleccionado */}
      {!selected ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">Selecciona un vídeo.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* Cabecera de acciones */}
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <h3 className="truncate font-heading text-lg font-bold" data-testid="vs-selected-title">{selected.title}</h3>
                <div className="mt-1 flex items-center gap-3">
                  <StatusBadge status={effStatus} />
                  {dirty && <span className="text-xs text-amber-600">· cambios sin guardar</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="vs-save-btn">
                  {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Guardar guion
                </Button>
                <Button size="sm" onClick={() => gen.mutate()} disabled={isRunning || dirty || gen.isPending} data-testid="vs-generate-btn"
                  title={dirty ? "Guarda los cambios antes de generar" : ""}>
                  {isRunning || gen.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}Generar vídeo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Progreso de generación */}
          {(isRunning || status.data?.log_tail) && (
            <Card className={isRunning ? "border-amber-300" : ""}>
              <CardContent className="p-4">
                <p className="mb-2 text-xs font-semibold text-muted-foreground">Registro de generación</p>
                {status.data?.error && <p className="mb-2 text-xs text-destructive">{status.data.error}</p>}
                <pre className="max-h-44 overflow-auto whitespace-pre-wrap rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed text-foreground/80" data-testid="vs-log">
{status.data?.log_tail || "Iniciando…"}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Vista previa del vídeo actual */}
          {selected.video_exists && !isRunning && (
            <Card><CardContent className="p-4">
              <p className="mb-2 text-xs font-semibold text-muted-foreground">Vídeo actual</p>
              <video key={selected.video_url} controls preload="metadata" className="w-full rounded-lg border bg-black" data-testid="vs-preview-video">
                <source src={selected.video_url} />
              </video>
            </CardContent></Card>
          )}

          {/* Editor de pasos */}
          <div className="space-y-3">
            {steps.map((step, si) => (
              <Card key={si} data-testid={`vs-step-${si}`}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-start gap-2">
                    <Badge variant="secondary" className="mt-1.5 shrink-0">{si + 1}</Badge>
                    <Textarea value={step.narration} onChange={(e) => setStep(si, { narration: e.target.value })}
                      rows={2} placeholder="Texto que se locuta en este paso…" className="text-sm" data-testid={`vs-step-${si}-narration`} />
                    <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(si, -1)} disabled={si === 0} data-testid={`vs-step-${si}-up`}><ChevronUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(si, 1)} disabled={si === steps.length - 1} data-testid={`vs-step-${si}-down`}><ChevronDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => delStep(si)} data-testid={`vs-step-${si}-delete`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>

                  {/* Acciones del paso */}
                  <div className="ml-8 space-y-2 border-l pl-3">
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <MousePointerClick className="h-3.5 w-3.5" />Acciones en pantalla
                    </p>
                    {(step.actions || []).map((a, ai) => (
                      <div key={ai} className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2" data-testid={`vs-step-${si}-action-${ai}`}>
                        <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                        <Select value={a.op} onValueChange={(v) => setActionOp(si, ai, v)}>
                          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`vs-step-${si}-action-${ai}-op`}><SelectValue /></SelectTrigger>
                          <SelectContent>{OP_KEYS.map((k) => <SelectItem key={k} value={k}>{OPS[k].label}</SelectItem>)}</SelectContent>
                        </Select>
                        {(OPS[a.op]?.fields || []).map((f) => (
                          <Input key={f.k} type={f.type || "text"} value={a[f.k] ?? ""} placeholder={f.ph}
                            onChange={(e) => setActionField(si, ai, f.k, f.type === "number" ? Number(e.target.value) : e.target.value)}
                            className="h-8 min-w-[150px] flex-1 text-xs" data-testid={`vs-step-${si}-action-${ai}-${f.k}`} />
                        ))}
                        <Input type="number" value={a.wait ?? ""} placeholder="espera (ms)"
                          onChange={(e) => setActionField(si, ai, "wait", Number(e.target.value))}
                          className="h-8 w-28 text-xs" data-testid={`vs-step-${si}-action-${ai}-wait`} />
                        <div className="flex items-center">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveAction(si, ai, -1)} disabled={ai === 0}><ChevronUp className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveAction(si, ai, 1)} disabled={ai === step.actions.length - 1}><ChevronDown className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => delAction(si, ai)} data-testid={`vs-step-${si}-action-${ai}-delete`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addAction(si)} data-testid={`vs-step-${si}-add-action`}>
                      <Plus className="mr-1 h-3.5 w-3.5" />Añadir acción
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" onClick={addStep} className="w-full" data-testid="vs-add-step">
              <Plus className="mr-1.5 h-4 w-4" />Añadir paso
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
